import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Field } from 'src/API';
import { FieldInput, FIELD_TYPES, processModuleApi } from 'src/api/process-module';
import { useSelector } from 'src/store';
import logger from 'src/log/log';

export interface RootCanvasFieldData {
  fieldId: string;
  fieldName: string;
  fieldLabel: string;
  canvasId: string;
  templateId?: string;
  formId: string;
  formName: string;
  isRootField?: boolean;
  referencedFieldId?: string;
}

export interface RootCanvasFieldState {
  mode: 'root' | 'reference';
  isLoading: boolean;
  rootFields: RootCanvasFieldData[];
  error: string | null;
  selectedRootField?: RootCanvasFieldData;
}

export interface RootCanvasFieldProperties {
  type: typeof FIELD_TYPES.PROCESS_MODULE_ROOT_CANVAS;
  canvasId?: string;
  templateId?: string;
  canvasCreated?: boolean;
  isRootField?: boolean;
  referencedFieldId?: string;
  override?: {
    label?: string;
  };
  access?: 'editor' | 'manager';
}

export const useProcessModuleRootCanvas = (
  field: FieldInput & Partial<Field>,
  moduleId: string,
  onFieldChange: (updates: Partial<FieldInput & Field>) => void,
  allFormFields?: (FieldInput & Partial<Field>)[],
) => {
  const [state, setState] = useState<RootCanvasFieldState>({
    mode: 'root',
    isLoading: false,
    rootFields: [],
    error: null,
    selectedRootField: undefined,
  });

  const currentTeam = useSelector((state) => state.team.current);
  const processModuleState = useSelector((state) => state.processModule);

  // Get organization ID
  const organizationId = processModuleState.processModules.list.find((pm) => pm.id === moduleId)?.managerGroups?.[0];

  // Fetch existing root canvas fields
  const fetchRootCanvasFields = useCallback(async () => {
    if (!currentTeam?.id || !moduleId || !organizationId) return;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await processModuleApi.getProcessModuleCanvasFields(moduleId, organizationId);

      if (response.ok && response.result?.canvasFields) {
        logger.log('Root canvas fields - filtering:', {
          currentFieldId: field.id,
          allCanvasFields: response.result.canvasFields,
          allFormFieldsCount: allFormFields?.length,
        });

        // Filter for root canvas fields only, excluding current field
        const serverRootFields = response.result.canvasFields.filter((canvas: any) => {
          const isNotCurrentField = canvas.fieldId !== field.id;
          const isRootField = canvas.isRootField === true;
          const shouldInclude = isNotCurrentField && isRootField;

          logger.log('Filtering server root field:', {
            fieldId: canvas.fieldId,
            currentFieldId: field.id,
            isNotCurrentField,
            isRootField,
            shouldInclude,
          });

          return shouldInclude;
        });

        // Add locally created root canvas fields that aren't saved yet
        const localRootFields: RootCanvasFieldData[] = [];
        if (allFormFields) {
          allFormFields.forEach((formField) => {
            const isNotCurrentField = formField.id !== field.id;
            const isRootCanvasType = formField.type === FIELD_TYPES.PROCESS_MODULE_ROOT_CANVAS;
            const isRootField = formField.properties?.isRootField === true;
            const hasCanvasId = !!formField.properties?.canvasId;
            const isCanvasCreated = formField.properties?.canvasCreated === true;
            const notInServerResults = !serverRootFields.some((serverField) => serverField.fieldId === formField.id);

            logger.log('Filtering local root field:', {
              fieldId: formField.id,
              currentFieldId: field.id,
              isNotCurrentField,
              isRootCanvasType,
              isRootField,
              hasCanvasId,
              isCanvasCreated,
              notInServerResults,
            });

            // Check if it's a root canvas field that's not the current field
            if (
              isRootCanvasType &&
              isNotCurrentField &&
              isRootField &&
              hasCanvasId &&
              isCanvasCreated &&
              notInServerResults
            ) {
              localRootFields.push({
                fieldId: formField.id || '',
                fieldName: formField.name || '',
                fieldLabel: formField.label || 'Root Canvas Field',
                canvasId: formField.properties?.canvasId || '',
                templateId: formField.properties?.templateId,
                formId: 'current-form', // Placeholder since it's not saved yet
                formName: 'Current Form', // Placeholder since it's not saved yet
              });
            }
          });
        }

        // Combine server and local root canvas fields
        const allRootFields = [...serverRootFields, ...localRootFields];

        logger.log('Root canvas fields - final result:', {
          serverRootFieldsCount: serverRootFields.length,
          localRootFieldsCount: localRootFields.length,
          totalRootFields: allRootFields.length,
          rootFieldIds: allRootFields.map((rf) => rf.fieldId),
        });

        setState((prev) => ({
          ...prev,
          rootFields: allRootFields,
          isLoading: false,
        }));

        // Set mode based on current field state
        const referencedFieldId = field.properties?.referencedFieldId;
        const selectedRoot = allRootFields.find((rf) => rf.fieldId === referencedFieldId);
        if (referencedFieldId && selectedRoot) {
          setState((prev) => ({
            ...prev,
            mode: 'reference',
            selectedRootField: selectedRoot,
          }));
        } else if (field.properties?.isRootField === true) {
          setState((prev) => ({
            ...prev,
            mode: 'root',
          }));
        }
      } else {
        setState((prev) => ({
          ...prev,
          rootFields: [],
          isLoading: false,
          error: 'Failed to load existing root canvases',
        }));
      }
    } catch (error) {
      logger.error('Failed to fetch root canvas fields', error);
      setState((prev) => ({
        ...prev,
        rootFields: [],
        isLoading: false,
        error: 'Failed to load existing root canvases',
      }));
    }
  }, [currentTeam?.id, moduleId, organizationId, field.id, field.properties?.referencedFieldId, allFormFields]);

  // Initialize and fetch data
  useEffect(() => {
    fetchRootCanvasFields();
  }, [fetchRootCanvasFields]);

  // Get current canvas properties with defaults
  const getRootCanvasProperties = useCallback((): RootCanvasFieldProperties => {
    return {
      type: FIELD_TYPES.PROCESS_MODULE_ROOT_CANVAS,
      canvasId: field.properties?.canvasId || '',
      templateId: field.properties?.templateId || '',
      canvasCreated: field.properties?.canvasCreated || false,
      isRootField: field.properties?.isRootField || false,
      referencedFieldId: field.properties?.referencedFieldId || '',
      override: field.properties?.override || {},
      access: field.properties?.access || 'editor',
    };
  }, [field.properties]);

  // Update canvas properties
  const updateRootCanvasProperties = useCallback(
    (updates: Partial<RootCanvasFieldProperties>) => {
      const currentProps = getRootCanvasProperties();
      const newProperties = { ...currentProps, ...updates };

      onFieldChange({
        properties: newProperties,
      });
    },
    [getRootCanvasProperties, onFieldChange],
  );

  // Change canvas mode
  const setCanvasMode = useCallback(
    (mode: 'root' | 'reference') => {
      setState((prev) => ({ ...prev, mode, selectedRootField: undefined }));

      if (mode === 'root') {
        // Clear reference field data but keep template for root mode
        updateRootCanvasProperties({
          isRootField: true,
          referencedFieldId: undefined,
          canvasId: '',
          canvasCreated: false,
        });
        onFieldChange({
          dataSource: '',
          dataSourceType: undefined,
        });
      } else {
        // Clear root field data for reference mode
        updateRootCanvasProperties({
          isRootField: false,
          canvasId: '',
          templateId: '',
          canvasCreated: false,
        });
        onFieldChange({
          dataSource: '',
          dataSourceType: undefined,
        });
      }
    },
    [updateRootCanvasProperties, onFieldChange],
  );

  // Select root field for reference
  const selectRootField = useCallback(
    (rootFieldId: string) => {
      const selectedRoot = state.rootFields.find((rf) => rf.fieldId === rootFieldId);
      if (!selectedRoot) return;

      updateRootCanvasProperties({
        referencedFieldId: selectedRoot.fieldId,
        canvasId: selectedRoot.canvasId,
        templateId: selectedRoot.templateId,
        canvasCreated: true,
        isRootField: false,
      });

      onFieldChange({
        dataSource: selectedRoot.fieldId,
        dataSourceType: 'root_canvas_reference',
      });

      // Store the selected root field information
      setState((prev) => ({ ...prev, selectedRootField: selectedRoot }));

      // Check if field needs to be saved
      const needsSave = field.id.startsWith('temp-');
      if (needsSave) {
        toast.success('Root field reference configured! Remember to click "Save Field" to persist this configuration.');
      } else {
        toast.success('Root field reference configured successfully!');
      }
    },
    [state.rootFields, updateRootCanvasProperties, onFieldChange, field.id],
  );

  // Configure root canvas (generate canvas ID for FormBuilder)
  const configureRootCanvas = useCallback(() => {
    const currentProps = getRootCanvasProperties();
    if (!currentProps.templateId) {
      toast.error('Please select a template first');
      return false;
    }

    // Generate a canvas ID for this root field
    const canvasId = `pic-root-field-${field.id}`;

    // Mark as configured with generated canvas ID
    updateRootCanvasProperties({
      canvasId: canvasId,
      canvasCreated: true,
      isRootField: true,
    });

    // Check if field needs to be saved
    const needsSave = field.id.startsWith('temp-');
    if (needsSave) {
      toast.success('Root canvas configured! Remember to click "Save Field" to persist this configuration.');
    } else {
      toast.success('Root canvas configured successfully!');
    }
    return true;
  }, [getRootCanvasProperties, updateRootCanvasProperties, field.id]);

  // Validation
  const validate = useCallback(() => {
    const errors: string[] = [];
    const currentProps = getRootCanvasProperties();

    if (!field.label?.trim()) {
      errors.push('Root canvas field must have a label');
    }

    if (state.mode === 'root') {
      if (!currentProps.templateId) {
        errors.push('Please select a template');
      }
      if (!currentProps.canvasCreated) {
        errors.push('Please configure the root canvas');
      }
    } else {
      if (!currentProps.referencedFieldId) {
        errors.push('Please select a root field to reference');
      }
    }

    return errors;
  }, [field.label, getRootCanvasProperties, state.mode]);

  // Computed states
  const canCreateRootCanvas = Boolean(
    state.mode === 'root' && getRootCanvasProperties().templateId && !getRootCanvasProperties().canvasCreated,
  );

  const hasAvailableRootFields = state.rootFields.length > 0;

  return {
    // State
    state,

    // Properties
    properties: getRootCanvasProperties(),

    // Actions
    setCanvasMode,
    selectRootField,
    configureRootCanvas,
    updateRootCanvasProperties,

    // Computed
    canCreateRootCanvas,
    hasAvailableRootFields,
    validate,

    // Loading states
    isLoading: state.isLoading,
  };
};
